import React, { FC } from "react";
import {
	Box,
	Typography,
	makeStyles,
	useMediaQuery,
	useTheme,
} from "@material-ui/core";
import { CellSize } from "Components/Calendar/CalendarHelper";
import { useCalendar } from "Components/Common/hooks/useCalendar";
import { color } from "Configs/theme";
import moment from "moment";

interface Props {
	cellSize: CellSize;
}

function getHoursArray(workingMinutes: { start: number; end: number }) {
	const startHour = Math.floor(workingMinutes.start / 60);
	const endHour = Math.ceil(workingMinutes.end / 60);
	return Array(endHour - startHour)
		.fill(null)
		.map((_, index) => startHour + index);
}

const NavigationCalendarDayView: FC<Props> = (props) => {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const selectedDate = useCalendar((state) => state.selectedDate);
	const workingMinutes = useCalendar((state) => state.workingMinutes);
	const hours = getHoursArray(workingMinutes);
	const classes = useStyles({
		hourWidth: props.cellSize.day.hourWidth,
		nameWidth: props.cellSize.day.nameWidth,
		navigationCalendarHeight: props.cellSize.navigation.calendarHeight,
		hoursLength: hours.length,
	});

	function renderHourColumn(hour: number, index: number) {
		const currentHour = selectedDate.clone().set('hour', hour);
		const isLastColumn = index === hours.length - 1;

		return (
			<Box className={classes.dayColumn} key={hour}>
				<Box className={classes.timeContainer}>
					<Typography 
						variant="caption" 
						className={classes.startTime}
						style={{ color: moment().isSame(currentHour, "hour") ? color.primary : color.lightText }}
					>
						{hour}:00
					</Typography>
					{isLastColumn && (
						<Typography 
							variant="caption" 
							className={classes.endTime}
							style={{ color: color.lightText }}
						>
							{hour + 1}:00
						</Typography>
					)}
				</Box>
			</Box>
		);
	}

	return (
		<Box className={classes.root}>
			<Box className={classes.mainColumn}>
				{isSmallScreen && (
					<Box className={classes.mainMonthYear}>
						{selectedDate.format("MMM D, YYYY")}
					</Box>
				)}
			</Box>
			{hours.map((hour, index) => renderHourColumn(hour, index))}
		</Box>
	);
};

export default NavigationCalendarDayView;

interface StyleProps {
	hourWidth: number;
	nameWidth: number;
	navigationCalendarHeight: number;
	hoursLength: number;
}

const useStyles = makeStyles({
	root: {
		display: "flex",
		borderBottom: `1px solid ${color.tableCellBorder}`,
	},
	mainColumn: (props: StyleProps) => ({
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
		letterSpacing: "1px",
		fontSize: "16px",
		fontWeight: "bold",
		height: props.navigationCalendarHeight,
		width: props.nameWidth - 1,
	}),
	mainMonthYear: {
		fontSize: "12px",
		textAlign: "center",
		lineHeight: "1.3",
	},
	dayColumn: (props: StyleProps) => ({
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		borderLeft: `1px solid ${color.tableCellBorder}`,
		fontSize: "10px",
		fontWeight: 500,
		letterSpacing: "1px",
		textTransform: "uppercase",
		width: `calc((100% - ${props.nameWidth}px) / ${props.hoursLength})`,
		minWidth: "60px",
	}),
	timeContainer: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		height: "100%",
		padding: "4px 2px",
		position: "relative",
	},
	startTime: {
		fontSize: "12px",
		fontWeight: 500,
		position: "absolute",
		left: "4px",
		bottom: "4px",
	},
	endTime: {
		fontSize: "12px",
		fontWeight: 500,
		position: "absolute",
		right: "4px",
		bottom: "4px",
	},
});
